import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faSpinner,
  faTools,
  faExclamationTriangle,
  faDownload,
  faExternalLinkAlt,
  faCopy,
  faCheck,
  faPlay,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import BacktestLogs from './BacktestLogs';
import BacktestEquityDisplay, { BACKTEST_REPLAY_DURATION_MS } from './BacktestEquityDisplay';
import MarketEventsCoverage from './MarketEventsCoverage';
import useBacktestEquityCurve from '../hooks/useBacktestEquityCurve';
import './HistoryCard.css';
import { AI_AGENT_ROUTE, buildAiAgentBacktestUrlFromRecord, prepAiAgentHandoff } from '../utils/aiAgentNav';
import { copyBacktestDebugInfo, getBacktestErrorMessage } from '../utils/backtestDebugInfo';
import { normalizeBacktestResultStatus } from '../utils/backtestHelpers';
import {
  enhanceTradesHtmlWithFuturesMarkers,
  parseBacktestTradesCsv,
} from './BacktestIndicatorChart/enhanceTradesHtmlWithFuturesMarkers';

const formatRunContextValue = (value) => {
  if (typeof value === 'string') return value;
  if (value === null) return 'null';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

const titleCaseRunContext = (value) => String(value || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatObservedRoute = (route) => {
  const exchange = route?.exchange ? titleCaseRunContext(route.exchange) : null;
  const vendor = route?.vendor ? titleCaseRunContext(route.vendor) : null;
  const adapter = route?.adapter ? titleCaseRunContext(route.adapter.replace(/RoutingAdapter$/i, '')) : null;
  const source = [exchange, vendor || adapter].filter(Boolean).join(' via ');
  const context = [route?.symbol, route?.resolution].filter(Boolean).join(' · ');
  return [source || adapter || 'Recorded source', context].filter(Boolean).join(' — ');
};

/**
 * HistoryCard - Redesigned chart-first backtest results display
 *
 * Layout:
 * 1. Status/Version badges (compact header)
 * 2. Equity Curve Chart (hero element)
 * 3. Compact MetricsRow (primary metrics visible, secondary hidden)
 * 4. Period/Run On/Duration (subtle text)
 * 5. View Details button
 * 6. Downloads (collapsed by default)
 * 7. Logs toggle
 */
const HistoryCard = ({
  result,
  getApiToken,
  suppressManualNoTradeAlert = false,
  hideFixActions = false,
  softenManagedRepairAttempts = false,
  compactActions = null,
  onFixWithAI  // Optional callback for direct AI fix (bypasses navigation when on AI Agent page)
}) => {
  const navigate = useNavigate();
  const [loadingFiles, setLoadingFiles] = useState({});
  const [showLogs, setShowLogs] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [debugCopySuccess, setDebugCopySuccess] = useState(false);
  const [isReplayActive, setIsReplayActive] = useState(false);
  const [replayProgress, setReplayProgress] = useState(1);

  // Local file download error state (NOT shared with backtest error state)
  const [fileDownloadError, setFileDownloadError] = useState(null);

  // Portal tooltip state
  const [showStatusTooltip, setShowStatusTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const statusBadgeRef = useRef(null);
  const replayTimerRef = useRef(null);
  const replayChartDataRef = useRef([]);

  // Fetch equity curve data
  const { chartData, loading: chartLoading, error: chartError } = useBacktestEquityCurve(
    result.id,
    getApiToken
  );
  replayChartDataRef.current = chartData || [];

  const normalizedStatus = normalizeBacktestResultStatus(result);
  const isNoTradesStatus = normalizedStatus === 'completed_no_trades';
  const normalizedRescueStatus = String(result.rescueStatus || '').toLowerCase();
  const normalizedWakeStatus = String(
    result.wakeStatus || result.agentWakeStatus || result.rescueWakeStatus || ''
  ).toLowerCase();
  const normalizedAgentRunStatus = String(result.agentRunStatus || result.runStatus || '').toLowerCase();
  const activeWakeStatuses = new Set(['pending', 'claimed', 'delivering', 'running', 'pending_active_turn']);
  const activeAgentRunStatuses = new Set(['running', 'resuming']);
  const hasActiveRescuePlanItem = Boolean(
    result.hasInProgressRescuePlanItem ||
      result.inProgressRescuePlanItemId ||
      result.activeRescuePlanItemId
  );
  const hasNewerRescueBacktest = Boolean(
    result.newerRescueBacktestId ||
      result.latestRescueBacktestId ||
      result.activeRescueBacktestId
  );
  const isActiveRepairMode = Boolean(
    result.repairMode === true ||
      activeWakeStatuses.has(normalizedRescueStatus) ||
      activeWakeStatuses.has(normalizedWakeStatus) ||
      activeAgentRunStatuses.has(normalizedAgentRunStatus) ||
      hasActiveRescuePlanItem ||
      hasNewerRescueBacktest
  );
  const isManagedRepairAttempt = Boolean(
    isActiveRepairMode ||
      result.rescueTriggered ||
      result.rescueStatus ||
      result.repairReason
  );
  const useManagedRepairCopy = Boolean(
    isActiveRepairMode || (softenManagedRepairAttempts && isManagedRepairAttempt)
  );
  const isCompleted =
    normalizedStatus === 'completed' ||
    normalizedStatus === 'completed_with_errors' ||
    isNoTradesStatus;
  const isTimedOut = normalizedStatus === 'terminated_timeout';
  const isFailed = normalizedStatus === 'failed' || normalizedStatus === 'force_stopped' || isTimedOut;
  const hasStatsArtifact = Boolean(result.artifacts?.stats_csv || result.artifacts?.stats_parquet);
  const showResults =
    isCompleted ||
    hasStatsArtifact ||
    normalizedStatus === 'force_stopped' ||
    isTimedOut ||
    normalizedStatus === 'stopped';

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const totalReturnValue = toNumber(result.totalReturn);
  const cagrValue = toNumber(result.cagrAnnualReturn);
  const maxDrawdownValue = toNumber(result.maxDrawdown);

  const hasZeroReturn =
    isNoTradesStatus ||
    (isCompleted &&
      [totalReturnValue, cagrValue, maxDrawdownValue].every((val) => val === 0 || val === null));

  const hasFixActions = Boolean(result.aiStrategyId && result.revisionId);
  const canShowFixActions = hasFixActions && !hideFixActions && !useManagedRepairCopy;
  const errorMessage = useMemo(() => getBacktestErrorMessage(result), [result]);

  // Metrics for MetricsRow
  const metrics = useMemo(
    () => ({
      annualReturn: result.cagrAnnualReturn,
      maxDrawdown: result.maxDrawdown,
      totalReturn: result.totalReturn,
      metricDetails: result.metricDetails,
      sharpe: result.sharpe,
      sortino: result.sortino,
      romad: result.romad
    }),
    [result]
  );

  useEffect(() => {
    const replayData = replayChartDataRef.current;
    if (!isReplayActive || !replayData || replayData.length < 2) return undefined;

    const startTime = Date.now();

    const tick = () => {
      const nextProgress = Math.min((Date.now() - startTime) / BACKTEST_REPLAY_DURATION_MS, 1);
      setReplayProgress(nextProgress);

      if (nextProgress >= 1) {
        setIsReplayActive(false);
        if (replayTimerRef.current) {
          clearInterval(replayTimerRef.current);
          replayTimerRef.current = null;
        }
      }
    };

    setReplayProgress(0);
    replayTimerRef.current = setInterval(tick, 100);
    tick();

    return () => {
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
    };
  }, [isReplayActive]);

  const startReplay = useCallback(() => {
    const replayData = replayChartDataRef.current;
    if (!replayData || replayData.length < 2) return;
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
    setReplayProgress(0);
    setIsReplayActive(true);
  }, []);

  const handleReplayClick = useCallback((event) => {
    event?.preventDefault?.();
    startReplay();
  }, [startReplay]);

  const handleReplayKeyDown = useCallback((event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    startReplay();
  }, [startReplay]);

  // Format duration
  const formattedDuration = useMemo(() => {
    if (result.durationMs == null) return null;
    const ms = result.durationMs;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) {
      const mins = Math.floor(ms / 60000);
      const secs = Math.floor((ms % 60000) / 1000);
      return `${mins}m ${secs}s`;
    }
    const hrs = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    return `${hrs}h ${mins}m`;
  }, [result.durationMs]);

  // Format dates
  const periodText = useMemo(() => {
    if (!result.startDate || !result.endDate) return null;
    const start = moment(result.startDate).format('MMM D, YYYY');
    const end = moment(result.endDate).format('MMM D, YYYY');
    return `${start} → ${end}`;
  }, [result.startDate, result.endDate]);
  const hasEquityDisplay = Boolean(showResults && (chartLoading || chartError || chartData.length > 0));
  const showHeaderPeriod = Boolean(periodText && !hasEquityDisplay);

  const runOnText = useMemo(() => {
    if (!result.createdAt) return null;
    return moment(result.createdAt).format('MMM D, YYYY h:mm A');
  }, [result.createdAt]);

  const parameterEntries = useMemo(
    () => Object.entries(result.strategyParameters?.values || {}),
    [result.strategyParameters]
  );
  const observedRoutes = Array.isArray(result.dataProvenance?.observedRoutes)
    ? result.dataProvenance.observedRoutes
    : [];
  const selectedDataSource = result.dataProvenance?.selection
    || (result.routingPolicySnapshot ? 'BotSpot Auto' : null);
  const routingPolicyVersion = result.dataProvenance?.policyVersion
    || result.routingPolicySnapshot?.version
    || null;
  const showRunContext = parameterEntries.length > 0
    || Boolean(selectedDataSource)
    || Boolean(result.routingPolicySnapshot)
    || Boolean(result.dataProvenance);

  const handleFixError = useCallback(() => {
    if (!result.aiStrategyId || !result.revisionId) return;

    const fixPayload = {
      prompt: 'The backtest failed with an error. Please fix the strategy.',
      aiStrategyId: result.aiStrategyId,
      revisionId: result.revisionId,
      source: 'history-failed',
      context: {
        backtestId: result.id,
        status: result.status,
        error: errorMessage
      }
    };

    // If onFixWithAI callback is provided (on AI Agent page), use it directly
    // Otherwise, use the navigation pattern for other pages (e.g., /my-backtests)
    if (onFixWithAI) {
      onFixWithAI(fixPayload);
    } else {
      prepAiAgentHandoff(fixPayload);
      navigate(AI_AGENT_ROUTE, { replace: true });
    }
  }, [result, errorMessage, onFixWithAI, navigate]);

  const handleFixNoTrades = useCallback(() => {
    if (!result.aiStrategyId || !result.revisionId) return;

    const fixPayload = {
      prompt:
        "The backtest completed but returned zero percent with likely no trades executed. Please analyze why the strategy didn't trade and fix it.",
      aiStrategyId: result.aiStrategyId,
      revisionId: result.revisionId,
      source: 'history-no-trades',
      context: {
        backtestId: result.id,
        status: result.status,
        noTrades: true
      }
    };

    // If onFixWithAI callback is provided (on AI Agent page), use it directly
    // Otherwise, use the navigation pattern for other pages (e.g., /my-backtests)
    if (onFixWithAI) {
      onFixWithAI(fixPayload);
    } else {
      prepAiAgentHandoff(fixPayload);
      navigate(AI_AGENT_ROUTE, { replace: true });
    }
  }, [result, onFixWithAI, navigate]);

  const detailsHref = useMemo(() => {
    if (!result.id) return undefined;
    return buildAiAgentBacktestUrlFromRecord(result);
  }, [result]);
  const repairChatHref = useMemo(() => {
    const conversationId = [
      result.conversationId,
      result.externalConversationId,
      result.external_conversation_id
    ].find((candidate) => typeof candidate === 'string' && candidate.trim());
    return conversationId
      ? `${AI_AGENT_ROUTE}/${encodeURIComponent(conversationId.trim())}?mode=chat`
      : `${AI_AGENT_ROUTE}?mode=chat`;
  }, [result]);

  // Handler for copying debug info to clipboard
  const handleCopyDebugInfo = useCallback(async () => {
    try {
      await copyBacktestDebugInfo(result);
      setDebugCopySuccess(true);
      // Reset the checkmark after 2 seconds
      setTimeout(() => setDebugCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy debug info:', err);
    }
  }, [result]);

  const downloadOrOpenFile = async (endpoint, filename, shouldOpen = false) => {
    if (loadingFiles[endpoint]) return;

    try {
      setLoadingFiles((prev) => ({ ...prev, [endpoint]: true }));

      const token = await getApiToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/backtests/${result.id}/file/${endpoint}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error(`Failed to fetch ${filename}`);

      const data = await response.json();
      let content = data.content;
      if (endpoint === 'trades_html' && typeof content === 'string') {
        try {
          const csvResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/backtests/${result.id}/file/trades_csv`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          if (csvResponse.ok) {
            const csvData = await csvResponse.json();
            content = enhanceTradesHtmlWithFuturesMarkers(
              content,
              parseBacktestTradesCsv(csvData?.content || '')
            );
          }
        } catch (_enhanceErr) {
          // Keep the original LumiBot HTML if the CSV replay cannot run.
        }
      }
      const blob = new Blob([content], {
        type: filename.endsWith('.html') ? 'text/html' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);

      if (shouldOpen) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(`Error handling ${filename}:`, err);
      // Use LOCAL error state - do NOT use shared setError which would show as backtest failure
      setFileDownloadError(`Failed to download ${filename}`);
      // Auto-dismiss after 5 seconds
      setTimeout(() => setFileDownloadError(null), 5000);
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [endpoint]: false }));
    }
  };

  // Status badge formatting with tooltip explanation
  const statusText = useMemo(() => {
    const key = normalizeBacktestResultStatus(result);
    if (useManagedRepairCopy && (key === 'failed' || key === 'force_stopped' || key === 'terminated_timeout')) {
      return isActiveRepairMode ? 'Working' : 'Needs Attention';
    }
    if (useManagedRepairCopy && key === 'completed_no_trades') {
      return 'No Trades Yet';
    }
    if (key === 'terminated_timeout') return 'Stopped (Timed Out)';
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [result, useManagedRepairCopy, isActiveRepairMode]);

  const statusClassName = useManagedRepairCopy
    ? 'hc-status-managed-repair'
    : `hc-status-${normalizedStatus}`;
  const replayButtonText = isReplayActive
    ? `${Math.round(Math.max(0, Math.min(1, replayProgress)) * 100)}%`
    : 'Replay';

  // Status tooltip with explanation + duration
  const statusTooltipData = useMemo(() => {
    const explanations = {
      completed: 'Backtest finished successfully.',
      completed_no_trades: 'Backtest finished but no trades were executed.',
      failed: 'Backtest encountered an error and stopped.',
      force_stopped: 'Backtest was manually stopped.',
      terminated_timeout: 'Backtest stopped because it exceeded the runtime cap.',
      running: 'Backtest is currently in progress.',
      pending: 'Backtest is queued and will start soon.',
      not_found: 'Backtest results could not be found.'
    };
    return {
      text: explanations[normalizedStatus] || 'Unknown status',
      duration: formattedDuration ? `Completed in ${formattedDuration}` : null
    };
  }, [normalizedStatus, formattedDuration]);

  // Handle tooltip hover - calculate position and show via portal
  const handleStatusMouseEnter = useCallback(() => {
    if (statusBadgeRef.current) {
      const rect = statusBadgeRef.current.getBoundingClientRect();
      // Position tooltip below the badge (to avoid clipping at top)
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
      setShowStatusTooltip(true);
    }
  }, []);

  const handleStatusMouseLeave = useCallback(() => {
    setShowStatusTooltip(false);
  }, []);

  const isLinkedExecution = result?.isLinkedStrategy === true || !!result?.linkedStrategyId;

  // Get all non-HTML artifact files for "View Files" toggle
  // These are files NOT shown in the main downloads section (tearsheet_html, trades_html)
  // NOTE: indicators_html removed for legal/compliance reasons
  const mainHtmlFiles = useMemo(() => {
    return ['tearsheet_html', 'trades_html'];
  }, []);
  const additionalFiles = useMemo(() => {
    if (!result.artifacts) return [];
    return Object.entries(result.artifacts)
      .filter(([key, value]) => {
        if (!value) return false;
        if (mainHtmlFiles.includes(key)) return false;
        if (isLinkedExecution && (key === 'trades_csv' || key === 'trades_html')) {
          return false;
        }
        // ALWAYS exclude indicators files - legal/compliance requirement
        if (key === 'indicators_csv' || key === 'indicators_html' || key === 'indicators_parquet') {
          return false;
        }
        return true;
      })
      .map(([key]) => key);
  }, [result.artifacts, mainHtmlFiles, isLinkedExecution]);

  const hasAdditionalFiles = additionalFiles.length > 0;

  // Helper to format artifact key into display name
  const formatFileName = useCallback((key) => {
    // Convert snake_case to Title Case and handle extensions
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/Csv$/, 'CSV')
      .replace(/Html$/, 'HTML');
  }, []);

  // Helper to get file extension for download
  const getFileExtension = useCallback((key) => {
    if (key.endsWith('_csv')) return '.csv';
    if (key.endsWith('_html')) return '.html';
    return '';
  }, []);

  return (
    <div className="backtest-history-card hc-redesign">
      {/* Compact Header: All on one line - Status, Version, Period, Run date */}
      <div className="hc-header-compact">
        <span
          ref={statusBadgeRef}
          className={`hc-status-badge ${statusClassName}`}
          onMouseEnter={handleStatusMouseEnter}
          onMouseLeave={handleStatusMouseLeave}
        >
          {statusText}
        </span>

        {/* Portal tooltip - renders outside DOM hierarchy to avoid overflow clipping */}
        {showStatusTooltip && ReactDOM.createPortal(
          <div
            className="hc-status-tooltip-portal"
            style={{
              position: 'fixed',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateX(-50%)',
              zIndex: 10000
            }}
          >
            <span className="hc-status-tooltip-text">{statusTooltipData.text}</span>
            {statusTooltipData.duration && (
              <span className="hc-status-tooltip-duration">{statusTooltipData.duration}</span>
            )}
          </div>,
          document.body
        )}
        {/* Version badge - check both direct and nested revision.version */}
        {(result.version || result.revision?.version) && (
          <span className="hc-version-badge" title={`Strategy Version ${result.version ?? result.revision?.version} - Each time your strategy code is modified, a new version is created. This backtest used this specific version of your strategy.`}>
            v{result.version ?? result.revision?.version}
          </span>
        )}
        {showHeaderPeriod && <span className="hc-period-inline">{periodText}</span>}
        {runOnText && <span className="hc-run-date-inline" title={`This backtest was executed on ${runOnText}`}>Ran {runOnText}</span>}
        {showResults && !chartLoading && !chartError && chartData.length > 1 && (
          <button
            className={`hc-replay-btn ${isReplayActive ? 'hc-replay-btn--active' : ''}`}
            type="button"
            onClick={handleReplayClick}
            onKeyDown={handleReplayKeyDown}
            aria-label="Replay account value chart"
            title="Replay account value chart"
          >
            <FontAwesomeIcon icon={faPlay} />
            <span>{replayButtonText}</span>
          </button>
        )}
      </div>

      {/* Failed Backtest Alert */}
      {isFailed && (
        <section
          className={`hc-alert ${useManagedRepairCopy ? 'hc-alert--managed' : 'hc-alert--failure'}`}
          role="status"
          aria-live="polite"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="hc-alert-icon" />
          <div className="hc-alert-content">
            <strong>
              {useManagedRepairCopy
                ? isActiveRepairMode
                  ? 'Spot is working on this backtest'
                  : 'Backtest needs attention'
                : normalizedStatus === 'terminated_timeout'
                ? 'Backtest Stopped (Timed Out)'
                : normalizedStatus === 'force_stopped'
                ? 'Backtest Stopped'
                : 'Backtest needs attention'}
            </strong>
            {useManagedRepairCopy && (
              <p className="hc-alert-text">
                {hasNewerRescueBacktest
                  ? 'A follow-up backtest is in progress. Open chat to review what Spot changed and monitor the new run.'
                  : isActiveRepairMode
                  ? 'Spot is reviewing the failure in chat. It will change the strategy only if the evidence points to a strategy issue.'
                  : 'Spot finished its review. Open chat to inspect what changed and see the latest backtest status.'}
              </p>
            )}
            {errorMessage && (
              <p className="hc-error-details">
                <span className="hc-error-label">Reported reason</span>
                {errorMessage}
              </p>
            )}
            {isManagedRepairAttempt && (
              <div className="hc-alert-actions">
                <Link className="hc-chat-btn" to={repairChatHref}>
                  <FontAwesomeIcon icon={faComments} />
                  {isActiveRepairMode ? 'View in chat' : 'Review in chat'}
                </Link>
              </div>
            )}
            {canShowFixActions && (
              <button
                className="btn btn-success btn-sm hc-fix-btn"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleFixError();
                }}
              >
                <FontAwesomeIcon icon={faTools} className="me-2" />
                Fix Using AI
              </button>
            )}
          </div>
        </section>
      )}

      {/* Equity Curve Chart (Hero) - Show whenever results artifacts exist (even for stopped/timeouts) */}
      {showResults && (
        <BacktestEquityDisplay
          mode={isReplayActive ? 'replay' : 'completed'}
          points={chartData}
          result={result}
          replayProgress={replayProgress}
          progressPercent={isReplayActive ? replayProgress * 100 : null}
          metrics={metrics}
          loading={chartLoading}
          error={chartError}
          height={400}
          showMetrics
          metricsSize="hero"
          className="hc-equity-display"
        />
      )}

      {showRunContext && (
        <section className="hc-run-context" aria-label="Backtest run configuration">
          {parameterEntries.length > 0 && (
            <div className="hc-run-context__group">
              <strong>Parameters used</strong>
              <div className="hc-run-context__chips">
                {parameterEntries.map(([key, value]) => (
                  <span className="hc-run-context__chip" key={key}>
                    <span>{key}</span>
                    <code>{formatRunContextValue(value)}</code>
                  </span>
                ))}
              </div>
            </div>
          )}
          {(selectedDataSource || result.routingPolicySnapshot || result.dataProvenance) && (
            <div className="hc-run-context__group">
              <strong>Data used</strong>
              <div className="hc-run-context__data-lines">
                {selectedDataSource && <span>Selected: {selectedDataSource}</span>}
                {observedRoutes.length > 0 ? observedRoutes.map((route, index) => (
                  <span key={`${route.symbol || 'route'}-${index}`}>
                    Actually used: {formatObservedRoute(route)}
                  </span>
                )) : (
                  <span>Actual source not recorded for this historical run</span>
                )}
                {routingPolicyVersion && <span>Policy {routingPolicyVersion}</span>}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Zero Return Warning */}
      {hasZeroReturn && !(suppressManualNoTradeAlert && !useManagedRepairCopy) && (
        <section className="hc-alert hc-alert--attention" role="status" aria-live="polite">
          <FontAwesomeIcon icon={faExclamationTriangle} className="hc-alert-icon" />
          <div className="hc-alert-content">
            <strong>{useManagedRepairCopy ? 'BotSpot is improving this backtest' : 'No Trading Activity'}</strong>
            <p className="hc-alert-text">
              {useManagedRepairCopy
                ? 'No filled trades were found yet, so the agent is continuing the repair loop in the conversation.'
                : 'No trades were executed in this run.'}
            </p>
            {canShowFixActions && (
              <button
                className="btn btn-success btn-sm hc-fix-btn"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleFixNoTrades();
                }}
              >
                <FontAwesomeIcon icon={faTools} className="me-2" />
                Fix Using AI
              </button>
            )}
          </div>
        </section>
      )}
      {/* Market Events Coverage */}
      {isCompleted && result.marketEventsCovered?.length > 0 && (
        <MarketEventsCoverage startDate={result.startDate} endDate={result.endDate} />
      )}

      {/* File Download Error - local to this card, NOT a backtest failure */}
      {fileDownloadError && (
        <div className="hc-file-download-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{fileDownloadError}</span>
          <button
            type="button"
            className="hc-file-download-error-dismiss"
            onClick={() => setFileDownloadError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Compact actions row — View Details, Tearsheet, Trades, Logs, Files all inline */}
      <div className="hc-compact-actions">
        {/* View Details link */}
        {showResults && detailsHref && (
          <Link className="hc-compact-action hc-compact-action--primary" to={detailsHref}>
            <FontAwesomeIcon icon={faChartLine} />
            <span>View Details</span>
          </Link>
        )}
        {/* Tearsheet */}
        {result.artifacts?.tearsheet_html && (
          <button
            type="button"
            className={`hc-compact-action ${loadingFiles['tearsheet_html'] ? 'loading' : ''}`}
            onClick={() => downloadOrOpenFile('tearsheet_html', 'tearsheet.html', true)}
            disabled={loadingFiles['tearsheet_html']}
          >
            <FontAwesomeIcon
              icon={loadingFiles['tearsheet_html'] ? faSpinner : faExternalLinkAlt}
              className={loadingFiles['tearsheet_html'] ? 'fa-spin' : ''}
            />
            <span>Tearsheet</span>
          </button>
        )}
        {/* Trades - hidden for linked strategy executions (non-author) */}
        {!isLinkedExecution && result.artifacts?.trades_html && (
          <button
            type="button"
            className={`hc-compact-action ${loadingFiles['trades_html'] ? 'loading' : ''}`}
            onClick={() => downloadOrOpenFile('trades_html', 'trades.html', true)}
            disabled={loadingFiles['trades_html']}
          >
            <FontAwesomeIcon
              icon={loadingFiles['trades_html'] ? faSpinner : faExternalLinkAlt}
              className={loadingFiles['trades_html'] ? 'fa-spin' : ''}
            />
            <span>Trades</span>
          </button>
        )}
        {/* View Logs */}
        <button
          type="button"
          className={`hc-compact-action ${showLogs ? 'hc-compact-action--active' : ''}`}
          onClick={() => setShowLogs(!showLogs)}
        >
          {showLogs ? 'Hide Logs' : 'View Logs'}
        </button>
        {/* View Files */}
        {hasAdditionalFiles && (
          <button
            type="button"
            className={`hc-compact-action ${showFiles ? 'hc-compact-action--active' : ''}`}
            onClick={() => setShowFiles(!showFiles)}
          >
            {showFiles ? 'Hide Files' : 'View Files'}
          </button>
        )}
        {compactActions}
        {/* Debug copy */}
        <button
          type="button"
          className={`hc-compact-action hc-compact-action--icon ${debugCopySuccess ? 'success' : ''}`}
          onClick={handleCopyDebugInfo}
          title="Copy backtest debug info for AI tools"
        >
          <FontAwesomeIcon icon={debugCopySuccess ? faCheck : faCopy} />
        </button>
      </div>

      {/* Files Expanded - shows all additional files (CSVs, etc.) when toggled */}
      {showFiles && hasAdditionalFiles && (
        <div className="hc-files-expanded">
          <div className="hc-files-grid">
            {additionalFiles.map((fileKey) => {
              const isHtml = fileKey.endsWith('_html');
              const fileName = fileKey.replace(/_/g, '.').replace(/\.(csv|html)$/, '') + getFileExtension(fileKey);
              return (
                <button
                  key={fileKey}
                  type="button"
                  className={`hc-compact-action ${loadingFiles[fileKey] ? 'loading' : ''}`}
                  onClick={() => downloadOrOpenFile(fileKey, fileName, isHtml)}
                  disabled={loadingFiles[fileKey]}
                >
                  <FontAwesomeIcon
                    icon={loadingFiles[fileKey] ? faSpinner : (isHtml ? faExternalLinkAlt : faDownload)}
                    className={loadingFiles[fileKey] ? 'fa-spin' : ''}
                  />
                  <span>{formatFileName(fileKey)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Logs Content - only when expanded */}
      <BacktestLogs
        backtestId={result.id}
        getApiToken={getApiToken}
        expanded={showLogs}
        onToggle={() => setShowLogs(!showLogs)}
        hideToggleButton={true}
      />
    </div>
  );
};

export default HistoryCard;
